import userService from '@/services/user-service'
import { isManager as computeIsManager } from '@/utils/task-permissions'
import { filterToManagerReports } from '@/utils/manager-id-matcher'

/**
 * True when `member` should count as active — same intent as the
 * `status === 'Active' || status === 'active' || ...` chain used
 * elsewhere in this codebase (e.g. `teamDataMixin.js#loadTeamMembers`),
 * but fixing two edge cases that chain gets wrong when it's written as
 * `member.status || member.employee_status || member.is_active` feeding a
 * single `status` variable:
 *
 *   1. **A higher-priority field explicitly `false`/`0`/`''` gets
 *      skipped, not honored.** `||` short-circuits on FALSY, not on
 *      "field is present" — so `member.status === undefined` but
 *      `member.is_active === false` (an explicit, correctly-populated
 *      "this person is inactive" boolean) would fall through the `||`
 *      chain, land on `member.is_active` too... but `false || anything`
 *      keeps falling through past it, ending in `status = undefined`,
 *      which the OLD final fallback (`status === undefined && emp_id`)
 *      then incorrectly treated as "no data, default active." This
 *      function instead finds the FIRST field that is actually PRESENT
 *      (not just truthy) and evaluates only that one.
 *   2. **`null` wasn't treated the same as `undefined`.** The old
 *      fallback checked `status === undefined` specifically — a row with
 *      `status: null` (field exists in the schema, just never populated)
 *      fell through as neither a recognized active value NOR eligible for
 *      the "no data, default active" fallback, and was silently excluded.
 *      This function's "is present" check treats `null`/`undefined`/`''`
 *      as equally "not really set."
 */
// Per explicit product direction: only the literal 'Active' status counts —
// same convention as the Attendance module's own `teamDataMixin.js#loadTeamMembers`
// (kept in lockstep with it on purpose, so both modules agree on "active").
function isActiveMember(member) {
  const candidates = [member.status, member.employee_status, member.is_active]
  const present = candidates.find(value => value !== undefined && value !== null && value !== '')
  if (present === undefined) return !!member.emp_id
  return present === 'Active' || present === 'active' || present === 'ACTIVE' || present === true
}

/**
 * Loads the logged-in user's direct reports.
 *
 * DATA SOURCE — `userService.getAllUsers()`, the SAME endpoint (`GET
 * user`) Attendance's own Team View uses for its non-account-manager path
 * (see `src/views/dashboard/attendance/mixins/teamDataMixin.js` ->
 * `loadTeamMembers()`'s `canApplyOnBehalfAll` branch), NOT
 * `getAllTeamMembersList()` (`GET users/team-members?emp_id=...`).
 *
 * CONFIRMED BY RUNTIME EVIDENCE (BUG FIX — this was the actual root cause
 * of "manager has 8 real direct reports, only 4/some show up"): the
 * `users/team-members` endpoint does not just over-scope ACLs, it returns
 * rows for employee ids that DON'T EXIST in the real `user` table at all.
 * Cross-checking a live capture: `users/team-members?emp_id=SH-002`
 * returned 196 rows, 7 of them tagged `manager: "SH-002"` — but the SAME
 * manager's true employee list from `getAllUsers()` (31 rows total, the
 * whole company) only contains 2 of those 7 (`ATS`, `SH-091`); the other 5
 * (e.g. `SH-213`, `SH-201`, `SH-145`, `SH-214`, `SH-238`) are not real
 * employees — `users/team-members` is fabricating/duplicating records, not
 * just returning a too-broad ACL-scoped set. `getAllUsers()` is the only
 * one of the two endpoints known to return the actual employee table as-is.
 *
 * Since `getAllUsers()` is NOT manager-scoped (it's the whole company),
 * `loadTaskTeamMembers()` below narrows it to direct reports client-side via
 * the shared `filterToManagerReports()` helper (`src/utils/manager-id-
 * matcher.js`) — the same approach, and same helper, Attendance's
 * `teamDataMixin.js#handleAccountManagerChange()` uses for its own
 * `getAllUsers()`-based manager filtering (`trustRowsWithNoManagerField:
 * false`, since nothing here already scopes the response to one manager).
 */

export default {
  data() {
    return {
      taskTeamMembers: [],
      loadingTaskTeam: false,
      currentUserData: {},
    }
  },
  computed: {
    currentEmpId() {
      return this.currentUserData?.emp_id ?? this.currentUserData?.empId ?? null
    },
    isTaskManager() {
      return computeIsManager(this.taskTeamMembers)
    },
  },
  created() {
    this.currentUserData = JSON.parse(localStorage.getItem('userData')) || {}
  },
  methods: {
    async loadTaskTeamMembers() {
      if (!this.currentEmpId) {
        this.taskTeamMembers = []
        return
      }
      this.loadingTaskTeam = true
      try {
        const res = await userService.getAllUsers()
        let allUsers = []
        if (res && res.status === 'OK') {
          allUsers = res.data || []
        } else if (res && Array.isArray(res.data)) {
          allUsers = res.data
        } else if (Array.isArray(res)) {
          allUsers = res
        }

        // `trustRowsWithNoManagerField: false` — `getAllUsers()` is the
        // WHOLE company, not scoped to this manager at all, so a row with
        // no manager-id field carries no evidence it's actually a direct
        // report (same convention as Attendance's own getAllUsers()-based
        // manager filtering in `teamDataMixin.js#handleAccountManagerChange`).
        const myReports = filterToManagerReports(allUsers, this.currentEmpId, '[task]', false)

        this.taskTeamMembers = myReports.filter(isActiveMember)

        // Diagnostic kept ALWAYS ON (not gated by NODE_ENV) — the app is
        // served from a production build here, so a dev-only `console.info`
        // never actually reaches the browser console.
        console.info(
          `[task] loadTaskTeamMembers(${this.currentEmpId}): getAllUsers returned ${allUsers.length} row(s) -> ${myReports.length} direct report(s) -> ${this.taskTeamMembers.length} active.`,
          { raw: allUsers, reports: myReports, final: this.taskTeamMembers },
        )
      } catch (err) {
        console.error('[task] Error loading team members:', err)
        this.taskTeamMembers = []
      } finally {
        this.loadingTaskTeam = false
      }
    },
    taskTeamMemberName(empId) {
      const member = this.taskTeamMembers.find(m => String(m.emp_id ?? m.id) === String(empId))
      if (!member) return null
      if (member.first_name) return `${member.first_name} ${member.last_name || ''}`.trim()
      return member.name || member.title || String(empId)
    },
    /**
     * Recipient email for the task-assignment notification (see
     * `task-service.js#notifyTaskAssigned`) — same `users` row shape as
     * `userData` in `auth-service.js` (which has `.email`), since
     * `getAllUsers` reads from the same `users` table. Returns
     * `null` (never throws) when the field isn't present so the caller can
     * skip the email gracefully instead of sending to `undefined`.
     */
    taskTeamMemberEmail(empId) {
      const member = this.taskTeamMembers.find(m => String(m.emp_id ?? m.id) === String(empId))
      return member?.email || null
    },
    /**
     * `users.id` for FCM token APIs (`GET/PUT /api/user/token/:id`) — distinct
     * from `emp_id`. Prefer the team-member row from `getAllUsers()`; fall
     * back to localStorage caches via `resolveUserIdFromEmpId` in fcm-push.
     */
    taskTeamMemberUserId(empId) {
      if (empId == null) return null
      // Match the same row lookup as taskTeamMemberName / taskTeamMemberEmail.
      const member = this.taskTeamMembers.find(m => String(m.emp_id ?? m.id) === String(empId))
      return member && member.id != null ? member.id : null
    },
  },
}
